import _ from 'lodash';
import { SetOptional } from 'type-fest';

import { Specialty } from './encounter';

export class APIError extends Error {
  payload: any;
  status: number;

  constructor(payload: any, status: number) {
    super(JSON.stringify({ ...payload, status }));
    this.payload = payload;
    this.status = status;
  }
}

export type Nullable<T> = { [P in keyof T]: T[P] | null };

export enum Environment {
  Dev = 'dev',
  Staging = 'staging',
  Demo = 'demo',
  Prod = 'prod',
  Weblocal = 'weblocal',
  Webprod = 'webprod',
  Webbucket = 'webbucket',
}

export interface BaseItem {
  id: string;
  created: string;
  updated: string;
}

export interface BaseEhrPatient {
  ehrInformationId: string;
  patientEhrId: string;
  patientId: string;
  practiceId: string;
}

export interface Pagination<T extends object> {
  results: T[];
  page: number;
  limit: number;
  count: number;
}

export interface PersistentItem {
  isDeleted: boolean;
  deletedTimestamp: Date | null;
}

export interface SuccessResponse {
  success: boolean;
  message?: string;
}

type KeysAccepting<T, U> = NonNullable<
  { [K in keyof T]: U extends T[K] ? K : never }[keyof T]
>;

// Makes all nullable fields optional
export type NullablePartial<T> = SetOptional<T, KeysAccepting<T, null>>;

export enum Relationship {
  AdoptedChild = 'ADOPTED_CHILD',
  Aunt = 'AUNT',
  AuntUncle = 'AUNT_UNCLE',
  Brother = 'BROTHER',
  CadaverDonor = 'CADAVER_DONOR',
  Child = 'CHILD',
  ChildInLaw = 'CHILD_IN_LAW',
  ChildNoFinancial = 'CHILD_NO_FINANCIAL',
  CollateralDependent = 'COLLATERAL_DEPENDENT',
  CourtAppointedGuardian = 'COURT_APPOINTED_GUARDIAN',
  Cousin = 'COUSIN',
  Daughter = 'DAUGHTER',
  DepChild = 'DEP_CHILD',
  DependentOfMinorDependent = 'DEPENDENT_OF_MINOR_DEPENDENT',
  DomesticPartnership = 'DOMESTIC_PARTNERSHIP',
  Donor = 'DONOR',
  EmancipatedMinor = 'EMANCIPATED_MINOR',
  Employee = 'EMPLOYEE',
  Employer = 'EMPLOYER',
  ExSpouse = 'EX_SPOUSE',
  Father = 'FATHER',
  FatherInLaw = 'FATHER_IN_LAW',
  FosterChild = 'FOSTER_CHILD',
  FosterParent = 'FOSTER_PARENT',
  Friend = 'FRIEND',
  Grandchild = 'GRANDCHILD',
  Granddaughter = 'GRANDDAUGHTER',
  Grandfather = 'GRANDFATHER',
  Grandmother = 'GRANDMOTHER',
  Grandparent = 'GRANDPARENT',
  Grandson = 'GRANDSON',
  GreatGrandfather = 'GREAT_GRANDFATHER',
  GreatGrandmother = 'GREAT_GRANDMOTHER',
  Guardian = 'GUARDIAN',
  HandicappedDependent = 'HANDICAPPED_DEPENDENT',
  InjuredPlaintiff = 'INJURED_PLAINTIFF',
  InLaw = 'IN_LAW',
  Legal = 'LEGAL_GUARDIAN',
  LifePartner = 'LIFE_PARTNER',
  Mother = 'MOTHER',
  MotherInLaw = 'MOTHER_IN_LAW',
  Neighbor = 'NEIGHBOR',
  NieceNephew = 'NIECE_NEPHEW',
  OrganDonor = 'ORGAN_DONOR',
  Other = 'OTHER',
  OtherAdult = 'OTHER_ADULT',
  OverageDependent = 'OVERAGE_DEPENDENT',
  Parent = 'PARENT',
  ParentInLaw = 'PARENT_IN_LAW',
  PersonalFamily = 'PERSONAL_FAMILY',
  Recipient = 'RECIPIENT',
  Relative = 'RELATIVE',
  Roommate = 'ROOMMATE',
  Self = 'SELF',
  Sibling = 'SIBLING',
  SiblingInLaw = 'SIBLING_IN_LAW',
  SignificantOther = 'SIGNIFICANT_OTHER',
  Sister = 'SISTER',
  Son = 'SON',
  Sponsor = 'SPONSOR',
  SponsoredDependent = 'SPONSORED_DEPENDENT',
  Spouse = 'SPOUSE',
  Stepchild = 'STEPCHILD',
  Stepparent = 'STEPPARENT',
  Stepfather = 'STEPFATHER',
  Stepmother = 'STEPMOTHER',
  Student = 'STUDENT',
  Uncle = 'UNCLE',
  Unknown = 'UNKNOWN',
  Unverified = 'UNVERIFIED_PROXY',
  Ward = 'WARD',
}

export enum Timezone {
  UTC = 'UTC',
  Honolulu = 'Pacific/Honolulu',
  Anchorage = 'America/Anchorage',
  LosAngeles = 'America/Los_Angeles',
  Phoenix = 'America/Phoenix',
  Denver = 'America/Denver',
  Chicago = 'America/Chicago',
  NewYork = 'America/New_York',
}

export enum TimingUnit {
  Minute = 'MINUTES',
  Hour = 'HOURS',
  Day = 'DAYS',
  BusinessDay = 'BUSINESS_DAYS',
  Week = 'WEEKS',
  Month = 'MONTHS',
  Year = 'YEARS',
}

export enum DayPeriod {
  Am = 'AM',
  Pm = 'PM',
}

export enum PhoneNumberKind {
  Cell = 'CELL',
  Work = 'WORK',
  Home = 'HOME',
}

export enum ModificationStatus {
  Added = 'ADDED',
  Removed = 'REMOVED',
  Unmodified = 'UNMODIFIED',
  Updated = 'UPDATED',
}

export enum AgeGroup {
  YOUNG = '<18',
  ADULT = '18-65',
  SENIOR = '65+',
}

export enum MaritalStatus {
  SINGLE = 'SINGLE',
  MARRIED = 'MARRIED',
}

export enum IncomeGroup {
  LESS_THAN_TEN_THOUSAND = '$10k/yr',
  TEN_THOUSAND_TO_FIFTY_THOUSAND = '$10k-$50k/yr',
  FIFTY_THOUSAND_TO_ONE_HUNDRED_THOUSAND = '$50k-$100k/yr',
  ONE_HUNDRED_THOUSAND_TO_TWO_HUNDRED_THOUSAND = '$100k-$200k/yr',
  GREATER_THAN_TWO_HUNDRED_THOUSAND = '$200k/yr+',
}

export enum Sex {
  Female = 'FEMALE',
  Male = 'MALE',
  NonBinary = 'NON_BINARY',
  Other = 'OTHER',
  Transgender = 'TRANSGENDER',
  TransgenderFemale = 'TRANSGENDER_FEMALE',
  TransgenderMale = 'TRANSGENDER_MALE',
  Unknown = 'UNKNOWN',
}

export const DefaultSexes = [Sex.Female, Sex.Male, Sex.Other];

export enum Gender {
  Female = 'FEMALE',
  Male = 'MALE',
  NonConforming = 'NON_CONFORMING',
  Other = 'OTHER',
}

export enum ProviderNameDisplayOption {
  Provider = 'PROVIDER',
  Practice = 'PRACTICE',
}

export type SortColumn<T> = keyof T;

export enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}

export type Sort<T> = {
  column: SortColumn<T>;
  order: SortOrder;
};

type Sorts<T> = Sort<T>[];

type Filter<T extends object> = Partial<{
  [K in keyof T]: T[K] extends string | null ? T[K] | T[K][] : undefined;
}>;

export type FindAll<
  Entity extends object,
  Sort extends BaseItem = BaseItem,
> = Partial<{
  filter: Filter<Entity>;
  sorts: Sorts<Sort>;
  limit: number;
  page: number;
}>;

export const SpecialtyMap: {
  [specialty in Specialty]: string;
} = {
  ACUPUNCTURE: 'Acupuncture',
  ALLERGY_AND_IMMUNOLOGY: 'Allergy and Immunology',
  ANESTHESIOLOGY: 'Anesthesiology',
  BARIATRIC_SURGERY: 'Bariatric Surgery',
  BEHAVIORAL_HEALTH: 'Behavioral Health',
  BURN_SURGERY: 'Burn Surgery',
  CARDIOLOGY: 'Cardiology',
  CRITICAL_CARE: 'Critical Care',
  DERMATOLOGY: 'Dermatology',
  DENTAL: 'Dental',
  EMERGENCY_MEDICINE: 'Emergency Medicine',
  ENDOCRINOLOGY: 'Endocrinology',
  ENT: 'Ears, Nose, and Throat',
  FAMILY_MEDICINE: 'Family Medicine',
  GASTROENTEROLOGY: 'Gastroenterology',
  GENERAL_SURGERY: 'General Surgery',
  GERIATRIC_MEDICINE: 'Geriatric Medicine',
  HEMATOLOGY_AND_ONCOLOGY: 'Hematology and Oncology',
  HEPATOLOGY: 'Hepatology',
  INFECTIOUS_DISEASE: 'Infectious Disease',
  INTERNAL_MEDICINE: 'Internal Medicine',
  MEDICAL_GENETICS: 'Medical Genetics',
  NEONATOLOGY: 'Neonatology',
  NEPHROLOGY: 'Nephrology',
  NEUROLOGICAL_SURGERY: 'Neurological Surgery',
  NEUROLOGY: 'Neurology',
  NUCLEAR_MEDICINE: 'Nuclear Medicine',
  OBGYN: 'OBGYN',
  OCCUPATIONAL_HEALTH: 'Occupational Health',
  OPHTHALMOLOGY: 'Ophthalmology',
  OPTOMETRY: 'Optometry',
  ORTHOPEDICS: 'Orthopedics',
  OTHER: 'Other',
  PAIN_MEDICINE: 'Pain Medicine',
  PALLIATIVE_CARE: 'Palliative Care',
  PATHOLOGY: 'Pathology',
  PEDIATRIC_SURGERY: 'Pediatric Surgery',
  PEDIATRICS: 'Pediatrics',
  PHYSICAL_MEDICINE_AND_REHABILITATION: 'Physical Medicine and Rehabilitation',
  PLASTIC_SURGERY: 'Plastic Surgery',
  PODIATRY: 'Podiatry',
  PRIMARY_CARE: 'Primary Care',
  PSYCHIATRY: 'Psychiatry',
  PULMONOLOGY: 'Pulmonology',
  RADIATION_ONCOLOGY: 'Radiation Oncology',
  RADIOLOGY: 'Radiology',
  RHEUMATOLOGY: 'Rheumatology',
  SLEEP_MEDICINE: 'Sleep Medicine',
  SPORTS_MEDICINE: 'Sports Medicine',
  STUDENT_MENTAL_HEALTH_AND_COUNSELING: 'Student Mental Health and Counseling',
  THORACIC_SURGERY: 'Thoracic Surgery',
  TOXICOLOGY: 'Toxicology',
  TRANSPLANT_SURGERY: 'Transplant Surgery',
  TRAUMA_SURGERY: 'Trauma Surgery',
  URGENT_CARE: 'Urgent Care',
  UROLOGY: 'Urology',
  VASCULAR_SURGERY: 'Vascular Surgery',
};

export const InvertedSpecialtyMap = _.invert(SpecialtyMap);
